"use client";

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

function enhanceExternalLinks(root: HTMLElement): void {
  root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
    const href = a.getAttribute("href") ?? "";
    if (!href.startsWith("http")) return;
    if (a.target === "_blank") return;
    a.target = "_blank";
    a.rel = `${a.rel ?? ""} noopener noreferrer`.trim();
  });
}

function enhanceCodeBlocks(root: HTMLElement): () => void {
  const cleanups: Array<() => void> = [];

  root.querySelectorAll("pre").forEach((pre) => {
    if (pre.querySelector("[data-help-copy-wrap]")) return;

    const wrap = document.createElement("div");
    wrap.setAttribute("data-help-copy-wrap", "true");
    wrap.className = "relative";

    const parent = pre.parentElement;
    if (!parent) return;

    parent.insertBefore(wrap, pre);
    wrap.appendChild(pre);
    pre.className = cn(pre.className, "pt-10");

    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = "复制";
    btn.className =
      "border-border bg-secondary text-secondary-foreground hover:bg-secondary/80 absolute right-2 top-2 z-10 inline-flex h-7 items-center rounded-md border px-2 text-xs font-medium shadow-xs transition-colors";

    const onClick = async (): Promise<void> => {
      const code = pre.querySelector("code")?.textContent ?? pre.textContent ?? "";
      try {
        await navigator.clipboard.writeText(code);
        btn.textContent = "已复制";
        window.setTimeout(() => {
          btn.textContent = "复制";
        }, 1600);
      } catch {
        btn.textContent = "失败";
        window.setTimeout(() => {
          btn.textContent = "复制";
        }, 1600);
      }
    };

    btn.addEventListener("click", onClick);
    wrap.appendChild(btn);

    cleanups.push(() => {
      btn.removeEventListener("click", onClick);
      wrap.replaceWith(pre);
    });
  });

  return () => {
    cleanups.forEach((fn) => {
      fn();
    });
  };
}

export function HelpEnhance({ rootSelector }: { rootSelector: string }): null {
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    const root = document.querySelector(rootSelector);
    if (!(root instanceof HTMLElement)) return;

    enhanceExternalLinks(root);
    const undoPre = enhanceCodeBlocks(root);

    cleanupRef.current = () => {
      undoPre();
    };

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = undefined;
    };
  }, [rootSelector]);

  return null;
}
